import { HttpTypes } from "@medusajs/types";

export const isVariantInStock = (v: HttpTypes.StoreProductVariant): boolean => {
  if (!v.manage_inventory) return true
  if (v.allow_backorder) return true
  return (v.inventory_quantity ?? 0) > 0
}

export const isSimpleProduct = (product: HttpTypes.StoreProduct): boolean => {
    return product.options?.length === 1 && product.options[0].values?.length === 1;
}

/** The size range a product is offered in, e.g. ["Small Adults", "Medium Adults", ...] */
export const getAvailableSizes = (
  product: Pick<HttpTypes.StoreProduct, "options">
): string[] => {
  const sizeOption = product.options?.find((o) => o.title === "Size")
  return (sizeOption?.values ?? []).map((v) => v.value)
}

/**
 * Converts Medusa handle to UKSS-compatible URL.
 * Example: p-466537-2025-2026-arsenal-home-mini-kit → /p-466537/2025-2026-arsenal-home-mini-kit.html
 */
export const getProductUrl = (handle: string | null | undefined): string => {
  if (!handle) return "#"
  const match = handle.match(/^p-(\d+)-(.+)$/)
  if (match) {
    return `/p-${match[1]}/${match[2]}.html`
  }
  return `/products/${handle}`
}

/**
 * `ukss_products_id` is the legacy UKSS product identifier, present on every
 * imported product and always equal to the `p-{id}` handle segment above.
 * `ukss_products_model` only exists on the older import batch (pre-Shopify
 * migration), so it's null on the bulk of the current catalog.
 */
export const getProductId = (
  product: Pick<HttpTypes.StoreProduct, "metadata">
): string | null => {
  const id = product.metadata?.ukss_products_id
  return typeof id === "number" || typeof id === "string" ? String(id) : null
}

// `mpath` (materialized path, e.g. "pcat_a.pcat_b.pcat_c") is a real field on
// the product category model but isn't declared on the SDK's StoreProductCategory type.
type CategoryWithMpath = HttpTypes.StoreProductCategory & { mpath?: string }

/**
 * Products are tagged with their full category ancestry as flat, separate
 * entries (Shopify/legacy import behaviour) alongside unrelated flat tag
 * categories (deals, "NEW ... kit", etc). The deepest entry by mpath depth
 * is the true leaf of the real hierarchy (e.g. "Home Shirt"); its parent
 * (e.g. "England") is the team/club-level category the reference site shows
 * as a single breadcrumb crumb.
 */
export const getBreadcrumbCategory = (
  categories: HttpTypes.StoreProductCategory[] | null | undefined
): HttpTypes.StoreProductCategory | null => {
  if (!categories?.length) return null

  const depth = (c: CategoryWithMpath) => c.mpath?.split(".").length ?? 0

  const deepest = (categories as CategoryWithMpath[]).reduce((a, b) =>
    depth(b) > depth(a) ? b : a
  )

  return deepest.parent_category ?? deepest
}

export type RelatedSearchGroup = HttpTypes.StoreProductCategory[]

/**
 * "Related Searches" on the reference site renders one pill per assigned
 * category, each showing its full ancestor chain (e.g. "Italian teams >
 * Juventus"). The one exception: a root category (no parent) that is itself
 * an ancestor of another assigned category is dropped as its own pill —
 * it already appears as the leading segment of that descendant's chain.
 * Root categories with no assigned descendant (deal/promo tags like "Black
 * Friday") keep their own single-segment pill.
 */
export const getRelatedSearchGroups = (
  categories: HttpTypes.StoreProductCategory[] | null | undefined
): RelatedSearchGroup[] => {
  if (!categories?.length) return []

  const list = categories as CategoryWithMpath[]
  const byId = new Map(list.map((c) => [c.id, c]))

  const ancestorIds = new Set(
    list.flatMap((c) => c.mpath?.split(".").slice(0, -1) ?? [])
  )

  const groups = list
    .filter((c) => c.parent_category || !ancestorIds.has(c.id))
    .map((c) => {
      const ids = c.mpath?.split(".") ?? [c.id]
      return ids
        .map((id) => byId.get(id))
        .filter((entry): entry is CategoryWithMpath => Boolean(entry))
    })

  // Hierarchy chains (team/club breadcrumbs) read before flat deal/promo tags,
  // shallowest chain first — matches the reference site's grouping.
  const hierarchy = groups
    .filter((g) => g.length > 1)
    .sort((a, b) => a.length - b.length)
  const flat = groups.filter((g) => g.length === 1)

  return [...hierarchy, ...flat]
}